/**
 * Motor de Engagement Inteligente
 *
 * Quatro tabs:
 *   Regras     — configuração e kill-switch de cada gatilho
 *   Hábitos    — cadências detectadas por utilizador+serviço
 *   Envios     — livro de registo com conversão e custo
 *   Simulação  — dry-run: quem seria notificado hoje, e com que texto
 *
 * A Simulação é o ecrã a usar ANTES de activar qualquer regra: mostra
 * candidatos, canal escolhido e mensagem final sem enviar nada.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  Cached as CachedIcon,
  Edit as EditIcon,
  PauseCircle as PauseCircleIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import service, {
  CANAIS,
  Canal,
  DispatchSummary,
  EnvioEngagement,
  EnvioStats,
  HabitoEngagement,
  HabitoStats,
  RegraEngagement,
} from '../../services/marketing-engagement.service';
import { EmptyState, PageHeader, StatCard } from '../../components/Marketing';

// ─── Constantes de apresentação ──────────────────────────────────────────────

const CORES_ESTADO: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  ACTIVO: 'success',
  IRREGULAR: 'warning',
  INACTIVO: 'default',
  CHURN: 'error',
};

const CORES_STATUS: Record<string, 'success' | 'error' | 'default'> = {
  ENVIADO: 'success',
  FALHOU: 'error',
  SUPRIMIDO: 'default',
};

const LABEL_TIPO: Record<string, string> = {
  RECORRENCIA: 'Recorrência',
  REACTIVACAO: 'Reactivação',
  CROSS_SELL: 'Cross-sell',
  ONBOARDING: 'Onboarding',
};

/** Por que motivo um lembrete não saiu — em linguagem que se entende. */
const LABEL_SUPRESSAO: Record<string, string> = {
  OPT_OUT: 'Recusou lembretes',
  PREF_CANAL: 'Canal desactivado nas preferências',
  COOLDOWN: 'Contactado recentemente',
  QUIET_HOURS: 'Fora da janela horária',
  CAP_CUSTO: 'Tecto de custo diário atingido',
  CAP_VOLUME: 'Tecto de volume diário atingido',
  SEM_CONTACTO: 'Sem contacto utilizável',
  SEM_TEMPLATE: 'Template não configurado',
  DUPLICADO: 'Já tinha sido enviado',
};

const CANAL_HELP: Record<Canal, string> = {
  INAPP: 'Custo zero. Escreve em pg_notificacao, que o app cliente já lê.',
  PUSH: 'Custo zero, mas exige firebase_messaging no app do consumidor.',
  EMAIL: 'Custo zero. Requer opt-in de marketing.',
  SMS: '~12 CVE por mensagem. Requer opt-in de marketing.',
  WHATSAPP: 'Exige template UTILITY aprovado pela Meta.',
};

const fmtData = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDataHora = (v: string | null) =>
  v
    ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const erroDe = (e: any, fallback: string) =>
  e?.response?.data?.error || e?.message || fallback;

// ─── Componente ──────────────────────────────────────────────────────────────

export default function Engagement() {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const notificar = useCallback(
    (msg: string, sev: 'success' | 'error' | 'info' = 'success') => setToast({ msg, sev }),
    []
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Motor de Engagement"
        subtitle="Lembretes de pagamento baseados no hábito de cada utilizador"
        icon={<AutoAwesomeIcon />}
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab icon={<SettingsIcon />} iconPosition="start" label="Regras" />
        <Tab icon={<BoltIcon />} iconPosition="start" label="Hábitos" />
        <Tab icon={<SendIcon />} iconPosition="start" label="Envios" />
        <Tab icon={<ScienceIcon />} iconPosition="start" label="Simulação" />
      </Tabs>

      {tab === 0 && <TabRegras onNotify={notificar} onError={setErro} />}
      {tab === 1 && <TabHabitos onNotify={notificar} onError={setErro} />}
      {tab === 2 && <TabEnvios onError={setErro} />}
      {tab === 3 && <TabSimulacao onNotify={notificar} onError={setErro} />}

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─── Tab: Regras ─────────────────────────────────────────────────────────────

interface TabProps {
  onNotify: (msg: string, sev?: 'success' | 'error' | 'info') => void;
  onError: (msg: string | null) => void;
}

function TabRegras({ onNotify, onError }: TabProps) {
  const [regras, setRegras] = useState<RegraEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [aEditar, setAEditar] = useState<RegraEngagement | null>(null);
  const [pausarOpen, setPausarOpen] = useState(false);
  const [aTestar, setATestar] = useState<RegraEngagement | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setRegras(await service.listRegras());
      onError(null);
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao carregar regras'));
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const setup = async () => {
    try {
      const r = await service.setup();
      onNotify(r.message || 'Schema pronto');
      carregar();
    } catch (e: any) {
      onError(erroDe(e, 'Erro no setup'));
    }
  };

  const toggle = async (regra: RegraEngagement) => {
    try {
      const r = await service.toggleRegra(regra.id);
      // Actualizar localmente em vez de recarregar tudo.
      setRegras((rs) => rs.map((x) => (x.id === regra.id ? { ...x, activo: r.activo } : x)));
      onNotify(r.message, r.activo ? 'success' : 'info');
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao alternar regra'));
    }
  };

  const pausarTudo = async () => {
    try {
      const r = await service.pausarTudo();
      setPausarOpen(false);
      onNotify(r.message, 'info');
      carregar();
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao pausar'));
    }
  };

  const activas = regras.filter((r) => r.activo).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<RefreshIcon />} onClick={carregar}>
          Actualizar
        </Button>
        <Button startIcon={<CachedIcon />} onClick={setup}>
          Criar/semear schema
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="error"
          variant="outlined"
          startIcon={<PauseCircleIcon />}
          onClick={() => setPausarOpen(true)}
          disabled={activas === 0}
        >
          Pausar tudo
        </Button>
      </Stack>

      {regras.length === 0 ? (
        <EmptyState
          title="Sem regras"
          description="Carregue em “Criar/semear schema” para criar as tabelas e as 4 regras default (todas inactivas)."
          action={
            <Button variant="contained" onClick={setup}>
              Criar schema
            </Button>
          }
        />
      ) : (
        <>
          {activas === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Nenhuma regra activa — nada está a ser enviado. Valide primeiro na tab{' '}
              <strong>Simulação</strong> e só depois active.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Activa</TableCell>
                  <TableCell>Regra</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Serviço</TableCell>
                  <TableCell>Canais (ordem de fallback)</TableCell>
                  <TableCell align="right">Antec.</TableCell>
                  <TableCell align="right">Cooldown</TableCell>
                  <TableCell align="right">Janela</TableCell>
                  <TableCell align="right">Tecto/dia</TableCell>
                  <TableCell align="right">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regras.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Switch checked={r.activo} onChange={() => toggle(r)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={LABEL_TIPO[r.tipo] || r.tipo} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {r.tipo === 'CROSS_SELL' && r.cross_sell_de
                        ? `${r.cross_sell_de} → ${r.cross_sell_para}`
                        : r.tipo_servico || 'todos'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {r.canais.map((c, i) => (
                          <Tooltip key={c} title={CANAL_HELP[c]}>
                            <Chip label={`${i + 1}. ${c}`} size="small" />
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {r.tipo === 'RECORRENCIA' ? `${r.antecedencia_dias}d` : '—'}
                    </TableCell>
                    <TableCell align="right">{r.cooldown_dias}d</TableCell>
                    <TableCell align="right">
                      {r.hora_inicio}h–{r.hora_fim}h
                    </TableCell>
                    <TableCell align="right">
                      {r.max_envios_dia}
                      {r.custo_max_diario_cve != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.custo_max_diario_cve} CVE
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Enviar exemplo para um contacto">
                        <IconButton size="small" onClick={() => setATestar(r)}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar configuração">
                        <IconButton size="small" onClick={() => setAEditar(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <DialogEditarRegra
        regra={aEditar}
        onClose={() => setAEditar(null)}
        onSaved={(r) => {
          setRegras((rs) => rs.map((x) => (x.id === r.id ? r : x)));
          setAEditar(null);
          onNotify('Regra actualizada');
        }}
        onError={onError}
      />

      <DialogTestar regra={aTestar} onClose={() => setATestar(null)} onNotify={onNotify} onError={onError} />

      <Dialog open={pausarOpen} onClose={() => setPausarOpen(false)}>
        <DialogTitle>Pausar todas as regras?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Vai desactivar as <strong>{activas}</strong> regra(s) activa(s). Nenhum lembrete será enviado
            até serem reactivadas manualmente. Os hábitos detectados mantêm-se.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPausarOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={pausarTudo}>
            Pausar tudo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Diálogo: editar regra ───────────────────────────────────────────────────

function DialogEditarRegra({
  regra,
  onClose,
  onSaved,
  onError,
}: {
  regra: RegraEngagement | null;
  onClose: () => void;
  onSaved: (r: RegraEngagement) => void;
  onError: (m: string) => void;
}) {
  const [form, setForm] = useState<Partial<RegraEngagement>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (regra) setForm({ ...regra });
  }, [regra]);

  if (!regra) return null;

  const set = (k: keyof RegraEngagement, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCanal = (c: Canal) => {
    const actuais = form.canais || [];
    set('canais', actuais.includes(c) ? actuais.filter((x) => x !== c) : [...actuais, c]);
  };

  const moverCanal = (c: Canal, delta: number) => {
    const actuais = [...(form.canais || [])];
    const i = actuais.indexOf(c);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= actuais.length) return;
    [actuais[i], actuais[j]] = [actuais[j], actuais[i]];
    set('canais', actuais);
  };

  const gravar = async () => {
    if (!form.canais?.length) {
      onError('Escolha pelo menos um canal');
      return;
    }
    setSaving(true);
    try {
      const r = await service.updateRegra(regra.id, form);
      onSaved(r);
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao gravar regra'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {regra.nome}
        <Typography variant="caption" color="text.secondary" display="block">
          {regra.codigo} · {LABEL_TIPO[regra.tipo]}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Nome"
              value={form.nome ?? ''}
              onChange={(e) => set('nome', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Descrição"
              value={form.descricao ?? ''}
              onChange={(e) => set('descricao', e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Grid>

          {/* Canais e ordem de fallback */}
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Canais — o dispatcher usa o primeiro elegível e pára
            </Typography>
            <Stack spacing={0.5}>
              {CANAIS.map((c) => {
                const activo = (form.canais || []).includes(c);
                const pos = (form.canais || []).indexOf(c);
                return (
                  <Stack key={c} direction="row" alignItems="center" spacing={1}>
                    <FormControlLabel
                      sx={{ minWidth: 140, m: 0 }}
                      control={<Switch size="small" checked={activo} onChange={() => toggleCanal(c)} />}
                      label={c}
                    />
                    {activo && (
                      <>
                        <Chip label={`prioridade ${pos + 1}`} size="small" />
                        <Button size="small" disabled={pos === 0} onClick={() => moverCanal(c, -1)}>
                          ↑
                        </Button>
                        <Button
                          size="small"
                          disabled={pos === (form.canais || []).length - 1}
                          onClick={() => moverCanal(c, 1)}
                        >
                          ↓
                        </Button>
                      </>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {CANAL_HELP[c]}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          {regra.tipo === 'RECORRENCIA' && (
            <>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Antecedência (dias)"
                  type="number"
                  value={form.antecedencia_dias ?? 0}
                  onChange={(e) => set('antecedencia_dias', Number(e.target.value))}
                  fullWidth
                  size="small"
                  helperText="Quantos dias antes da data prevista"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label="Tolerância (dias)"
                  type="number"
                  value={form.tolerancia_dias ?? 0}
                  onChange={(e) => set('tolerancia_dias', Number(e.target.value))}
                  fullWidth
                  size="small"
                  helperText="Até quantos dias depois ainda avisa"
                />
              </Grid>
            </>
          )}

          {regra.tipo === 'REACTIVACAO' && (
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Inactivo após (× intervalo)"
                type="number"
                inputProps={{ step: 0.5 }}
                value={form.inactivo_apos_multiplo ?? 2}
                onChange={(e) => set('inactivo_apos_multiplo', Number(e.target.value))}
                fullWidth
                size="small"
                helperText="2,0 = o dobro do intervalo habitual"
              />
            </Grid>
          )}

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Mín. pagamentos"
              type="number"
              value={form.min_pagamentos ?? 3}
              onChange={(e) => set('min_pagamentos', Number(e.target.value))}
              fullWidth
              size="small"
              helperText="Abaixo de 3 não há padrão"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Mín. confiança (0–1)"
              type="number"
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              value={form.min_confianca ?? 0.5}
              onChange={(e) => set('min_confianca', Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Intervalo mín. (dias)"
              type="number"
              value={form.intervalo_min_dias ?? 4}
              onChange={(e) => set('intervalo_min_dias', Number(e.target.value))}
              fullWidth
              size="small"
              helperText="Não incomodar quem paga todos os dias"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Intervalo máx. (dias)"
              type="number"
              value={form.intervalo_max_dias ?? 120}
              onChange={(e) => set('intervalo_max_dias', Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Cooldown (dias)"
              type="number"
              value={form.cooldown_dias ?? 7}
              onChange={(e) => set('cooldown_dias', Number(e.target.value))}
              fullWidth
              size="small"
              helperText="Por utilizador, nesta regra"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Hora início"
              type="number"
              inputProps={{ min: 0, max: 23 }}
              value={form.hora_inicio ?? 9}
              onChange={(e) => set('hora_inicio', Number(e.target.value))}
              fullWidth
              size="small"
              helperText="Hora de Cabo Verde"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Hora fim"
              type="number"
              inputProps={{ min: 0, max: 23 }}
              value={form.hora_fim ?? 20}
              onChange={(e) => set('hora_fim', Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Máx. envios/dia"
              type="number"
              value={form.max_envios_dia ?? 500}
              onChange={(e) => set('max_envios_dia', Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Tecto de custo/dia (CVE)"
              type="number"
              value={form.custo_max_diario_cve ?? ''}
              onChange={(e) =>
                set('custo_max_diario_cve', e.target.value === '' ? null : Number(e.target.value))
              }
              fullWidth
              size="small"
              helperText="Vazio = sem tecto"
            />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Código template SMS"
              value={form.template_sms_codigo ?? ''}
              onChange={(e) => set('template_sms_codigo', e.target.value || null)}
              fullWidth
              size="small"
              helperText="Editável em Configurações → SMS"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Código template Email"
              value={form.template_email_codigo ?? ''}
              onChange={(e) => set('template_email_codigo', e.target.value || null)}
              fullWidth
              size="small"
              helperText="Editável em Configurações → Email"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Título push"
              value={form.template_push_titulo ?? ''}
              onChange={(e) => set('template_push_titulo', e.target.value || null)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Template WhatsApp (nome aprovado na Meta)"
              value={form.template_whatsapp_nome ?? ''}
              onChange={(e) => set('template_whatsapp_nome', e.target.value || null)}
              fullWidth
              size="small"
              helperText="Sem template aprovado o canal é ignorado"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Corpo push"
              value={form.template_push_corpo ?? ''}
              onChange={(e) => set('template_push_corpo', e.target.value || null)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Título in-app"
              value={form.template_inapp_titulo ?? ''}
              onChange={(e) => set('template_inapp_titulo', e.target.value || null)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Corpo in-app"
              value={form.template_inapp_corpo ?? ''}
              onChange={(e) => set('template_inapp_corpo', e.target.value || null)}
              fullWidth
              size="small"
              multiline
              rows={2}
              helperText="Variáveis: {{nome}} {{servico}} {{referencia_mascarada}} {{intervalo}} {{dias_desde_ultimo}} {{valor_habitual}}"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={gravar} disabled={saving}>
          {saving ? 'A gravar…' : 'Gravar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Diálogo: enviar exemplo ─────────────────────────────────────────────────

function DialogTestar({
  regra,
  onClose,
  onNotify,
  onError,
}: {
  regra: RegraEngagement | null;
  onClose: () => void;
  onNotify: (m: string, s?: 'success' | 'error' | 'info') => void;
  onError: (m: string) => void;
}) {
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{
    resultados: Record<string, any>;
    previews: Record<string, string>;
  } | null>(null);

  useEffect(() => {
    if (regra) {
      setTelefone('');
      setEmail('');
      setResultado(null);
    }
  }, [regra]);

  if (!regra) return null;

  const enviar = async () => {
    if (!telefone && !email) {
      onError('Indique um telefone ou um email');
      return;
    }
    setEnviando(true);
    try {
      const r = await service.testar(regra.id, { telefone: telefone || undefined, email: email || undefined });
      setResultado(r);
      const falhas = Object.entries(r.resultados).filter(([, v]) => !v?.success);
      if (falhas.length === 0) onNotify('Exemplo enviado');
      else onNotify(`Alguns canais falharam: ${falhas.map(([k]) => k).join(', ')}`, 'error');
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao enviar exemplo'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar exemplo — {regra.nome}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Envia uma mensagem com dados fictícios para o contacto indicado. Não passa por supressões
          nem conta para as estatísticas.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Telefone (SMS)"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            size="small"
            placeholder="+238 999 9999"
            fullWidth
          />
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            type="email"
            fullWidth
          />
        </Stack>

        {resultado && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Texto renderizado
            </Typography>
            <Stack spacing={1}>
              {Object.entries(resultado.previews).map(([canal, texto]) => (
                <Paper key={canal} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {canal}
                  </Typography>
                  <Typography variant="body2">{texto}</Typography>
                </Paper>
              ))}
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Resultado do envio
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(resultado.resultados).map(([canal, r]: [string, any]) => (
                <Alert key={canal} severity={r?.success ? 'success' : 'error'}>
                  <strong>{canal}</strong>: {r?.success ? 'enviado' : r?.error || 'falhou'}
                </Alert>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={enviar} disabled={enviando}>
          {enviando ? 'A enviar…' : 'Enviar exemplo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tab: Hábitos ────────────────────────────────────────────────────────────

function TabHabitos({ onNotify, onError }: TabProps) {
  const [habitos, setHabitos] = useState<HabitoEngagement[]>([]);
  const [stats, setStats] = useState<HabitoStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recomputando, setRecomputando] = useState(false);
  const [filtros, setFiltros] = useState({ tipo_servico: '', estado: '', search: '' });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        service.listHabitos({
          tipo_servico: filtros.tipo_servico || undefined,
          estado: filtros.estado || undefined,
          search: filtros.search || undefined,
          limit: 100,
        }),
        service.habitoStats(),
      ]);
      setHabitos(h.data);
      setTotal(h.total);
      setStats(s);
      onError(null);
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao carregar hábitos'));
    } finally {
      setLoading(false);
    }
  }, [filtros, onError]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const recomputar = async (completo: boolean) => {
    setRecomputando(true);
    try {
      const r = await service.recomputar({ completo });
      onNotify(r.message);
      carregar();
    } catch (e: any) {
      onError(erroDe(e, 'Erro ao recomputar'));
    } finally {
      setRecomputando(false);
    }
  };

  const servicos = useMemo(() => stats?.porServico.map((s) => s.tipo_servico) ?? [], [stats]);

  return (
    <>
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard label="Hábitos detectados" value={stats.totais.habitos} color="primary" />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard
              label="Com cadência activa"
              value={stats.totais.activos}
              color="success"
              helper="Dentro do ritmo esperado"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StatCard
              label="Previstos nos próximos 7 dias"
              value={stats.totais.previstos7d}
              color="info"
              helper="Candidatos a lembrete"
            />
          </Grid>
        </Grid>
      )}

      {stats && stats.porServico.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Cadência média por serviço
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Serviço</TableCell>
                    <TableCell align="right">Hábitos</TableCell>
                    <TableCell align="right">Intervalo médio</TableCell>
                    <TableCell align="right">Confiança média</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.porServico.map((s) => (
                    <TableRow key={s.tipo_servico}>
                      <TableCell>{s.tipo_servico}</TableCell>
                      <TableCell align="right">{s.n}</TableCell>
                      <TableCell align="right">
                        {s.intervalo_mediano != null ? `${s.intervalo_mediano} dias` : '—'}
                      </TableCell>
                      <TableCell align="right">{(s.confianca_media * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Pesquisar"
          size="small"
          value={filtros.search}
          onChange={(e) => setFiltros((f) => ({ ...f, search: e.target.value }))}
          placeholder="serviço, entidade, referência"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Serviço</InputLabel>
          <Select
            label="Serviço"
            value={filtros.tipo_servico}
            onChange={(e) => setFiltros((f) => ({ ...f, tipo_servico: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {servicos.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={filtros.estado}
            onChange={(e) => setFiltros((f) => ({ ...f, estado: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {['ACTIVO', 'IRREGULAR', 'INACTIVO', 'CHURN'].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<CachedIcon />} onClick={() => recomputar(false)} disabled={recomputando}>
          Recalcular (recentes)
        </Button>
        <Button
          startIcon={<CachedIcon />}
          variant="outlined"
          onClick={() => recomputar(true)}
          disabled={recomputando}
        >
          Recalcular tudo
        </Button>
      </Stack>

      {recomputando && <LinearProgress sx={{ mb: 2 }} />}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : habitos.length === 0 ? (
        <EmptyState
          title="Sem hábitos detectados"
          description="Carregue em “Recalcular tudo” para analisar os últimos 12 meses de pagamentos. São precisos pelo menos 2 pagamentos do mesmo serviço e referência."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Utilizador</TableCell>
                <TableCell>Serviço</TableCell>
                <TableCell>Referência</TableCell>
                <TableCell align="right">Pagamentos</TableCell>
                <TableCell align="right">Intervalo</TableCell>
                <TableCell>Confiança</TableCell>
                <TableCell>Último</TableCell>
                <TableCell>Próximo previsto</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {habitos.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell>
                    <Typography variant="body2">{h.nome || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.user_id.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{h.servico_label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.tipo_servico}
                      {h.entidade_servico ? ` · ${h.entidade_servico}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{h.referencia_mascarada || '—'}</TableCell>
                  <TableCell align="right">{h.n_pagamentos}</TableCell>
                  <TableCell align="right">
                    {h.intervalo_mediano_dias != null ? `${Math.round(h.intervalo_mediano_dias)}d` : '—'}
                    {h.intervalo_mad_dias != null && (
                      <Tooltip title="Dispersão (MAD): quanto menor, mais regular">
                        <Typography variant="caption" color="text.secondary" display="block">
                          ±{Math.round(h.intervalo_mad_dias)}d
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 110 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, h.confianca * 100)}
                      color={h.confianca >= 0.6 ? 'success' : h.confianca >= 0.4 ? 'warning' : 'error'}
                      sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {(h.confianca * 100).toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell>{fmtData(h.ultimo_em)}</TableCell>
                  <TableCell>{fmtData(h.proximo_previsto_em)}</TableCell>
                  <TableCell>
                    <Chip label={h.estado} size="small" color={CORES_ESTADO[h.estado] || 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > habitos.length && (
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                A mostrar {habitos.length} de {total}. Use os filtros para refinar.
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </>
  );
}

// ─── Tab: Envios ─────────────────────────────────────────────────────────────

function TabEnvios({ onError }: { onError: (m: string | null) => void }) {
  const [envios, setEnvios] = useState<EnvioEngagement[]>([]);
  const [stats, setStats] = useState<EnvioStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ canal: '', status: '' });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        service.listEnvios({
          canal: filtros.canal || undefined,
          status: filtros.status || undefined,
          limit: 100,
        }),
        service.envioStats(30),
      ]);
      setEnvios(e.data);
      setTotal(e.total);
      setStats(s);
      onError(null);
    } catch (err: any) {
      onError(erroDe(err, 'Erro ao carregar envios'));
    } finally {
      setLoading(false);
    }
  }, [filtros, onError]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <>
      {stats && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label="Enviados (30d)" value={stats.totais.enviados} color="primary" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard label="Conversões" value={stats.totais.convertidos} color="success" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Taxa de conversão"
                value={`${stats.totais.taxa_conversao}%`}
                color="info"
                helper="Pagou nos 14 dias após o lembrete"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Custo"
                value={`${stats.totais.custo_cve} CVE`}
                color="warning"
                helper={
                  stats.totais.custo_por_conversao != null
                    ? `${stats.totais.custo_por_conversao} CVE por conversão`
                    : 'sem conversões ainda'
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Por canal
                  </Typography>
                  {stats.porCanal.length === 0 ? (
                    <EmptyState compact title="Sem dados" />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Canal</TableCell>
                          <TableCell align="right">Enviados</TableCell>
                          <TableCell align="right">Conversões</TableCell>
                          <TableCell align="right">Custo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.porCanal.map((c) => (
                          <TableRow key={c.canal}>
                            <TableCell>{c.canal}</TableCell>
                            <TableCell align="right">{c.enviados}</TableCell>
                            <TableCell align="right">
                              {c.convertidos}
                              {c.enviados > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({((c.convertidos / c.enviados) * 100).toFixed(0)}%)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{c.custo_cve} CVE</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Porque não foram enviados
                  </Typography>
                  {stats.porSupressao.length === 0 ? (
                    <EmptyState compact title="Nenhuma supressão" />
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {stats.porSupressao.map((s) => (
                          <TableRow key={s.motivo}>
                            <TableCell>{LABEL_SUPRESSAO[s.motivo] || s.motivo}</TableCell>
                            <TableCell align="right">{s.n}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Canal</InputLabel>
          <Select
            label="Canal"
            value={filtros.canal}
            onChange={(e) => setFiltros((f) => ({ ...f, canal: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {CANAIS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={filtros.status}
            onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {['ENVIADO', 'SUPRIMIDO', 'FALHOU'].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={carregar}>
          Actualizar
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : envios.length === 0 ? (
        <EmptyState
          title="Sem envios"
          description="Ainda não foi enviado nenhum lembrete. Todas as regras nascem inactivas."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quando</TableCell>
                <TableCell>Regra</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Mensagem / motivo</TableCell>
                <TableCell align="right">Custo</TableCell>
                <TableCell>Conversão</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {envios.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{fmtDataHora(e.enviado_em || e.criado_em)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{e.regra_nome || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.tipo}
                      {e.tipo_servico ? ` · ${e.tipo_servico}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={e.canal} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={e.status} size="small" color={CORES_STATUS[e.status] || 'default'} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    {e.status === 'SUPRIMIDO' ? (
                      <Typography variant="caption" color="text.secondary">
                        {LABEL_SUPRESSAO[e.motivo_supressao || ''] || e.motivo_supressao || '—'}
                      </Typography>
                    ) : (
                      <Tooltip title={e.erro || e.mensagem_preview || ''}>
                        <Typography variant="caption" noWrap display="block">
                          {e.erro || e.mensagem_preview || '—'}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{Number(e.custo_cve) > 0 ? `${e.custo_cve} CVE` : '—'}</TableCell>
                  <TableCell>
                    {e.converteu ? (
                      <Tooltip title={`${fmtDataHora(e.convertido_em)} · ${e.valor_convertido ?? '?'} CVE`}>
                        <Chip label="Converteu" size="small" color="success" />
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > envios.length && (
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                A mostrar {envios.length} de {total}.
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </>
  );
}

// ─── Tab: Simulação ──────────────────────────────────────────────────────────

function TabSimulacao({ onNotify, onError }: TabProps) {
  const [regras, setRegras] = useState<RegraEngagement[]>([]);
  const [regraId, setRegraId] = useState('');
  const [resumo, setResumo] = useState<DispatchSummary | null>(null);
  const [correndo, setCorrendo] = useState(false);

  useEffect(() => {
    service
      .listRegras()
      .then((rs) => {
        setRegras(rs);
        if (rs.length && !regraId) setRegraId(rs[0].id);
      })
      .catch((e) => onError(erroDe(e, 'Erro ao carregar regras')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simular = async () => {
    if (!regraId) return;
    setCorrendo(true);
    setResumo(null);
    try {
      const r = await service.simular(regraId, 100);
      setResumo(r.resumo);
      onNotify(r.message, 'info');
      onError(null);
    } catch (e: any) {
      onError(erroDe(e, 'Erro na simulação'));
    } finally {
      setCorrendo(false);
    }
  };

  const regra = regras.find((r) => r.id === regraId);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        A simulação corre exactamente o mesmo código do envio real — resolução de candidatos,
        supressões, escolha de canal e render do template — mas <strong>não envia nada</strong> e não
        escreve no histórico. Funciona com a regra desactivada. Use este ecrã antes de activar
        qualquer regra.
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Regra</InputLabel>
          <Select label="Regra" value={regraId} onChange={(e) => setRegraId(e.target.value)}>
            {regras.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.nome} {r.activo ? '' : '(inactiva)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={simular}
          disabled={!regraId || correndo}
        >
          {correndo ? 'A simular…' : 'Simular'}
        </Button>
        {regra && (
          <Typography variant="caption" color="text.secondary">
            Canais: {regra.canais.join(' → ')}
          </Typography>
        )}
      </Stack>

      {correndo && <LinearProgress sx={{ mb: 2 }} />}

      {resumo && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label="Candidatos" value={resumo.candidatos} color="primary" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label="Receberiam mensagem" value={resumo.enviados} color="success" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label="Seriam suprimidos" value={resumo.suprimidos} color="warning" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard label="Custo estimado" value={`${resumo.custoCve} CVE`} color="info" />
            </Grid>
          </Grid>

          {Object.keys(resumo.porSupressao).length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Motivos de supressão
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(resumo.porSupressao).map(([m, n]) => (
                    <Chip key={m} label={`${LABEL_SUPRESSAO[m] || m}: ${n}`} size="small" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {resumo.detalhe.length === 0 ? (
            <EmptyState
              title="Nenhum candidato hoje"
              description="Não há hábitos cuja data prevista caia na janela desta regra. Verifique se os hábitos já foram calculados na tab Hábitos."
            />
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Utilizador</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell>Mensagem que receberia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumo.detalhe.map((d, i) => (
                    <TableRow key={`${d.userId}-${i}`} hover>
                      <TableCell>
                        <Typography variant="body2">{d.nome || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {d.userId.slice(0, 8)}…
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {d.canal ? <Chip label={d.canal} size="small" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell>
                        {d.status === 'ENVIADO' ? (
                          <Chip label="Receberia" size="small" color="success" />
                        ) : (
                          <Tooltip title={LABEL_SUPRESSAO[d.motivoSupressao || ''] || ''}>
                            <Chip
                              label={LABEL_SUPRESSAO[d.motivoSupressao || ''] || d.status}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 460 }}>
                        <Typography variant="caption">{d.preview || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  );
}
